import { memo, useCallback, useRef } from 'react';

import { TaggingEvent } from '@/context/tagging';
import { buildCarTitle } from '@/helpers/normalizers';
import useTagging from '@/hooks/useTagging';
import type { ActionsConfig } from '@/types/ActionsConfig';
import type { Car } from '@/types/Car';
import type { FavoritesConfig } from '@/types/FavoritesConfig';
import type { GalleryConfig } from '@/types/GalleryConfig';
import type { TileSize } from '@/types/TileSize';

import ActionIconButton from '../ActionIconButton/ActionIconButton';
import FavoriteButton from '../FavoriteButton/FavoriteButton';
import ImageGallery from '../ImageGallery/ImageGallery';
import StatusBadge from '../StatusBadge/StatusBadge';
import { ActionButtons, Wrapper } from './Media.styles';

type MediaProps = {
  actions?: ActionsConfig;
  car: Car;
  favorites?: FavoritesConfig;
  gallery?: GalleryConfig;
  href: string;
  imageSrc: string;
  isFeatured: boolean;
  isUnavailable: boolean;
  tileSize: TileSize;
};

function Media({
  actions,
  car,
  favorites,
  gallery,
  href,
  imageSrc,
  isFeatured,
  isUnavailable,
  tileSize,
}: MediaProps) {
  /** Hooks */

  const { dispatchTaggingEvent } = useTagging();

  /** Refs */

  const galleryNavigated = useRef(false);

  /** Action Buttons */

  const actionButtonSize =
    tileSize === 'small' || tileSize === 'medium' ? 'small' : 'medium';

  const showFavoriteButton =
    favorites?.isFavorited !== undefined && favorites?.onChange !== undefined;

  const showMiniButton =
    tileSize === 'large' &&
    !isFeatured &&
    actions?.miniButton?.children !== undefined;

  const handleFavoriteClick = useCallback(
    (isFavorite: boolean) => {
      dispatchTaggingEvent({
        event: TaggingEvent.ClickFavoriteIconButton,
        data: {
          text: isFavorite ? 'save car' : 'remove from favorites',
          stockNumber: car.stockNumber,
          isFavorite,
        },
      });

      favorites?.onChange?.(isFavorite);
    },
    [car.stockNumber, dispatchTaggingEvent, favorites],
  );

  /** Images */

  const alt = buildCarTitle(car);

  const heroImage =
    imageSrc ||
    'https://img2.carmax.com/Images/fallback.jpg?width=400&height=300';

  const handleHeroImageClick = useCallback(() => {
    dispatchTaggingEvent({
      event: TaggingEvent.ClickCarTile,
      data: {
        text: alt,
      },
    });
  }, [alt, dispatchTaggingEvent]);

  const showImageGallery =
    (gallery?.enabled ?? true) &&
    !isUnavailable &&
    tileSize !== 'small' &&
    tileSize !== 'medium';

  const handleGalleryChange = useCallback(
    (index: number, prevIndex: number) => {
      if (galleryNavigated.current === false) {
        dispatchTaggingEvent({
          event: TaggingEvent.GalleryNavigate,
          data: { text: 'gallery' },
        });

        galleryNavigated.current = true;
      }

      gallery?.onChange?.(index, prevIndex);
    },
    [dispatchTaggingEvent, gallery],
  );

  /** Status Badge */

  const showStatusBadge = tileSize !== 'small';

  const statusBadgeSize =
    tileSize === 'medium' || tileSize === 'small' ? 'small' : undefined;

  return (
    <Wrapper className="kmx-car-tile__media">
      {showStatusBadge ? (
        <StatusBadge carStatus={car.statusInfo} size={statusBadgeSize} />
      ) : null}

      {showMiniButton || showFavoriteButton ? (
        <ActionButtons
          className="kmx-car-tile__media-action-buttons"
          direction="row"
          spacing={0.5}
          useFlexGap
        >
          {showMiniButton && actions?.miniButton?.children ? (
            <ActionIconButton size={actionButtonSize} {...actions?.miniButton}>
              {actions.miniButton.children}
            </ActionIconButton>
          ) : null}

          {showFavoriteButton ? (
            <FavoriteButton
              isFavorited={favorites.isFavorited}
              onClick={handleFavoriteClick}
              size={actionButtonSize}
              stockNumber={car.stockNumber}
            />
          ) : null}
        </ActionButtons>
      ) : null}

      {showImageGallery ? (
        <ImageGallery
          alt={alt}
          heroImageSrc={heroImage}
          href={href}
          loadAdditionalImages={gallery?.loadAdditionalImages}
          onChange={handleGalleryChange}
          onImagesLoaded={gallery?.onImagesLoaded}
          stockNumber={car.stockNumber}
          swiperProps={gallery?.swiperProps}
        />
      ) : (
        <a
          className="kmx-car-tile__hero-image"
          data-ignore-anchor-tag-rule
          href={href}
          rel="nofollow"
        >
          <img alt={alt} src={heroImage} onClick={handleHeroImageClick} />
        </a>
      )}
    </Wrapper>
  );
}

export default memo(Media);
